import {
  afterNextRender,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { A11yModule } from '@angular/cdk/a11y';
import { finalize } from 'rxjs';
import {
  ISkillCategory,
  ISkillCategoryChange,
  ISkillCategorySaveResponse,
} from '../../../shared/model/skill-management.model';
import { FaehigkeitenService } from '../../../services/faehigkeiten.service';
import { MessageService } from '../../../shared/components/message.service';
import { INPUT_LIMITS } from '../../../shared/constants/input-limits';
import {
  compareText,
  hasText,
  problemType,
  sameText,
} from '../skill-catalog-model';
import { SkillIconComponent } from '../skill-icon/skill-icon.component';

interface CategoryRow {
  rowId: number;
  id: string | null;
  originalDe: string;
  originalEn: string;
  de: string;
  en: string;
  skillCount: number;
}

@Component({
  selector: 'app-skill-categories-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslatePipe, A11yModule, SkillIconComponent],
  templateUrl: './skill-categories-modal.component.html',
  styleUrl: './skill-categories-modal.component.scss',
})
export class SkillCategoriesModalComponent implements OnInit {
  private readonly skillService = inject(FaehigkeitenService);
  private readonly messageService = inject(MessageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);

  @Input() categories: ISkillCategory[] = [];
  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<ISkillCategorySaveResponse>();

  rows: CategoryRow[] = [];
  saving = false;
  private deletedIds: string[] = [];
  private nextRowId = 0;

  get limits() {
    return INPUT_LIMITS;
  }

  ngOnInit(): void {
    this.rows = [...this.categories]
      .sort((a, b) => compareText(a.kategorie, b.kategorie))
      .map((category) => ({
        rowId: this.nextRowId++,
        id: category.id,
        originalDe: category.kategorie,
        originalEn: category.kategorieEn ?? '',
        de: category.kategorie,
        en: category.kategorieEn ?? '',
        skillCount: category.skillCount,
      }));
  }

  get missingEnCount(): number {
    return this.rows.filter((row) => !hasText(row.en)).length;
  }

  /** Only changed rows are sent, so untouched legacy rows may lack the English name. */
  get valid(): boolean {
    return this.rows
      .filter((row) => this.isChanged(row))
      .every(
        (row) =>
          hasText(row.de) &&
          hasText(row.en) &&
          !this.duplicateDeOf(row) &&
          !this.duplicateEnOf(row),
      );
  }

  /** The German name of the other row that already uses this German name, so the hint can name it. */
  duplicateDeOf(row: CategoryRow): string | null {
    const other =
      this.isChanged(row) && hasText(row.de)
        ? this.rows.find(
            (candidate) => candidate !== row && sameText(candidate.de, row.de),
          )
        : undefined;
    return other ? other.de.trim() || other.originalDe : null;
  }

  /** The German name of the other row that already uses this English name. */
  duplicateEnOf(row: CategoryRow): string | null {
    const other =
      this.isChanged(row) && hasText(row.en)
        ? this.rows.find(
            (candidate) => candidate !== row && sameText(candidate.en, row.en),
          )
        : undefined;
    return other ? other.de.trim() || other.originalDe : null;
  }

  isChanged(row: CategoryRow): boolean {
    return (
      !row.id ||
      row.de.trim() !== row.originalDe.trim() ||
      row.en.trim() !== row.originalEn.trim()
    );
  }

  isMissing(row: CategoryRow, value: string): boolean {
    return this.isChanged(row) && !hasText(value);
  }

  isRenamed(row: CategoryRow): boolean {
    return (
      !!row.id && hasText(row.de) && row.de.trim() !== row.originalDe.trim()
    );
  }

  addRow(): void {
    this.rows = [
      {
        rowId: this.nextRowId++,
        id: null,
        originalDe: '',
        originalEn: '',
        de: '',
        en: '',
        skillCount: 0,
      },
      ...this.rows,
    ];
    afterNextRender(
      () =>
        this.host.nativeElement
          .querySelector<HTMLInputElement>('.sk-catm__row input[lang="de"]')
          ?.focus(),
      { injector: this.injector },
    );
  }

  removeRow(row: CategoryRow): void {
    if (row.skillCount > 0) {
      return;
    }
    if (row.id) {
      this.deletedIds = [...this.deletedIds, row.id];
    }
    this.rows = this.rows.filter((other) => other !== row);
  }

  save(): void {
    if (!this.valid || this.saving) {
      return;
    }
    const categories: ISkillCategoryChange[] = this.rows
      .filter((row) => this.isChanged(row))
      .map((row) => ({
        id: row.id,
        kategorie: row.de.trim(),
        kategorieEn: row.en.trim(),
      }));
    if (categories.length === 0 && this.deletedIds.length === 0) {
      this.closed.emit();
      return;
    }
    this.saving = true;
    this.skillService
      .saveCategories({ categories, deletedIds: this.deletedIds })
      .pipe(
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          this.messageService.success('skillManagement.categories.saved');
          this.saved.emit(response);
        },
        error: (error: unknown) =>
          this.messageService.error(this.errorKey(error)),
      });
  }

  private errorKey(error: unknown): string {
    switch (problemType(error)) {
      case 'entity-name-already-exists':
        return 'skillManagement.categories.conflict';
      case 'skill-category-in-use':
        return 'skillManagement.categories.inUse';
      default:
        return error instanceof HttpErrorResponse && error.status === 404
          ? 'skillManagement.categories.outdated'
          : 'skillManagement.categories.saveError';
    }
  }
}
